import {Keying2Label, Keying2Opening, Keying2Product, Keying2Shortcode} from "../../../../api/Keying2";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import {IconButton2} from "../hardware/library/IconCell";
import React, {useContext, useEffect, useState} from "react";
import {ManagerLockContext} from "../ProjectActions";
import {api} from "../../../../api/API";
import {ReloadContext} from "../hardware/ReloadContext";
import {useSnackAction} from "../hardware/SnackAction";
import {KeyingCheckbox} from "./KeyingCheckbox";
import {IconPopupItem} from "../../../logistics/routes/IconPopupItem";
import {css} from "@emotion/css";
import {grey} from "@mui/material/colors";
import {InfoOutlined, KeyboardArrowDown, ModeEdit} from "@mui/icons-material";
import {ucWords} from "../pricing/PriceRequestSubmittal";
import {columnCount, theadCssName} from "./Keying2";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {lookupClass, popperClass} from "../hardware/OpeningLookup";
import ChevronRight from "@mui/icons-material/ChevronRight";
import {contains} from "nate-react-api-helpers";
import {AutoComplete} from "../openings/AutoComplete";
import {MiscProduct} from "../../../../api/MiscProducts";
import {useProjectId} from "../ProjectName";
import {KeyingEditingContext} from "./KeyingEditingContextProvider";
import {useFocusMode} from "../hardware/UseFocusMode";
import {Button, Chip, CircularProgress, Tooltip} from "@mui/material";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {useConfirm} from "../../../../misc/Confirm";
import {EditNameDialog} from "./EditNameDialog";

export function ShortCode(props: {
    value: Keying2Shortcode
    labels: Keying2Label[];
}) {
    const {locked} = useContext(ManagerLockContext);
    const reload = useContext(ReloadContext);
    const editCtx = useContext(KeyingEditingContext);

    const isEditingOther = !!editCtx.editing && editCtx.editing?.id !== props.value.id;
    const isEditing = editCtx.editing?.id === props.value.id;

    const [headerRowEl, setHeaderRowEl] = useState<HTMLTableRowElement | null>(null);

    let offsetY = 40;
    if(headerRowEl && isEditing) {
        try {
            let thead: Element | null = headerRowEl;
            while (thead && thead.tagName !== "TABLE") {
                thead = thead.parentElement;
            }

            if (thead) {
                thead = thead.getElementsByClassName(theadCssName)[0];
                offsetY = thead.clientHeight;
            }
        } catch (e) {
            console.error(e);
        }
    }

    useFocusMode({
        enabled: isEditing,
        focusRef: headerRowEl,
        offsetY: offsetY, // based on fixed header height
        allowScroll: true,
        adjustFocus: (row: HTMLElement, retryLater: () => void): Element[] => {
            const table = row.parentElement;
            if (!table) {
                retryLater();
                return [];
            }

            const rows = Array.from(table.children);
            let hasStart = false;
            const filteredRows: Element[] = [];

            for (let i = 0; i < rows.length; i++) {
                if (rows[i] === headerRowEl) {
                    hasStart = true;
                    filteredRows.push(rows[i]);
                    continue;
                }

                if (!hasStart) continue;

                // Stop when we reach the next shortcode header row
                const rowEl = (rows[i] as HTMLElement);
                if (rowEl.dataset.shortcodeHeader || !rowEl.classList.contains(keyingShortCodeCls)) {
                    break;
                }

                filteredRows.push(rows[i]);
            }

            return filteredRows;
        },
    });

    const [editName, setEditName] = useState(false);

    const [value, setValue] = useState<Keying2Shortcode>(props.value);
    const propsValue = props.value;
    const editAdd = isEditing ? editCtx.editing?.add : undefined;

    useEffect(() => {
        if (!isEditing || !editAdd) return;

        setValue(old => {
            const openings = (old.openings || []).slice(0);
            const miscItems = (old.miscItems || []).slice(0);

            if(editAdd.opening && !openings.find(v => v.id === editAdd.opening)) {
                openings.push({
                    id: editAdd.opening,
                    name: editAdd.name,
                    locationTwo: editAdd.locationTwo,
                    baseBuilding: editAdd.baseBuilding,
                } as any)
            }

            if(editAdd.miscProduct && !miscItems.find(v => v.id === editAdd.miscProduct)) {
                miscItems.push({
                    id: editAdd.miscProduct,
                    name: editAdd.name,
                } as any)
            }

            return {
                ...old,
                openings,
                miscItems
            }
        });
    }, [editAdd, isEditing]);

    useEffect(() => {
        if (!isEditing) {
            setValue(propsValue);
        }
    }, [propsValue, isEditing]);

    const updateShortCode = useSnackAction(async (input) => {
        await api.keying2.upsertShortcode(input);
        reload.emitter.emit(Date.now());
    }, {
        success: "Updated",
    });

    const save = useSnackAction(async (input: Keying2Shortcode) => {
        await api.keying2.updateShortcodeDetailed(input);
        reload.emitter.emit(Date.now());
        editCtx.onDone();
    }, {
        success: "Updated",
    });

    const products = (value.products || [] as Keying2Product[]);
    const all = products?.find(p => !p.product) as Keying2Product | undefined;
    const productBase = products.filter(p => !!p.product && !p.openingPart);

    const [showAll, setShowAll] = useState(false)
    const searchForOpenings = (value.openings || []).length > 0 || (value.miscItems || []).length === 0;
    const projectId = useProjectId();
    const confirm = useConfirm();

    const editValue: typeof setValue = (value) => {
        if (!isEditing) {
            editCtx.onEdit({
                id: props.value.id,
            });
        }

        setValue(value);
    }

    if(isEditingOther) return null;

    return (
        <>
            <tr
                ref={setHeaderRowEl}
                className={headerRow + " " + keyingShortCodeCls}
                data-shortcode-header="true"
            >
                <td style={{
                    fontWeight: "500",
                    paddingLeft: leftIndent,
                }} colSpan={columnCount}>
                    <div style={{display: "flex",
                        alignItems: "center",
                        gap: 4,
                    }}>
                        <div>
                            #{value.name}
                        </div>
                        <IconButton2 disabled={locked} style={{height: "1.2rem"}} onClick={async () => {
                            const ok = await confirm.confirm("Archive this shortcode?")
                            if(!ok) return;

                            updateShortCode.callback({
                                ...props.value,
                                archived: true,
                            })
                        }}>
                            <RemoveCircleOutline fontSize="inherit"/>
                        </IconButton2>
                        <IconButton2 disabled={locked} style={{height: "1.2rem"}} onClick={async () => {
                            setEditName(true)
                        }}>
                            <ModeEdit fontSize="inherit"/>
                        </IconButton2>
                        {editName && <EditNameDialog value={props.value} onClose={() => setEditName(false)} />}
                    </div>
                </td>
                <td colSpan={props.labels.length} style={{
                }}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        justifyContent: "flex-end",
                        minWidth: 300,
                    }}>

                        <IconButton2 style={{height: 16, marginRight: 10}}>
                            <AddCircleOutlineIcon fontSize="inherit"/>
                        </IconButton2>
                        {searchForOpenings ? <AutoComplete<Keying2Opening>
                            onSelect={o => {
                                if (value.openings?.find(v => v.id === o.id)) return;

                                editValue(old => Object.assign({}, old, {
                                    openings: [...(old.openings || []), o],
                                }));
                            }}
                            onMultiSelect={o => {
                                o = o.filter(oo => !value.openings?.find(v => v.id === oo.id));

                                editValue(old => Object.assign({}, old, {
                                    openings: [...(old.openings || []), ...o],
                                }))
                            }}
                            search={async (search) => {
                                const rs = await api.keying2.searchOpenings({
                                    project: projectId,
                                    search: search,
                                });

                                const ignore = value.openings?.map(i => i.id) || [];

                                return rs.filter(d => !contains(ignore, d.id));
                            }}
                            className={lookupClass}
                            popperClassName={openingPopperClass}
                            value={null}
                            equals={(a, b) => a.id === b.id}
                            makeLabel={a => a.name}
                            makeLabelReact={a => <OpeningLabel value={a} />}
                            useNativeInput
                            placeholder="Search for opening to add"
                            style={{
                                backgroundColor: "transparent",
                            }}
                        /> : <AutoComplete<MiscProduct & {
                            name: string;
                        }>
                            search={async (search) => {
                                const rs = await api.miscProducts.list({
                                    project: projectId,
                                });

                                const ignore = value.miscItems?.map(i => i.id) || [];

                                return rs.data.filter(d => !contains(ignore, d.id))
                                    .map(d => ({
                                        ...d,
                                        name: `#${d.id} - ${d.categoryName} ${d.productCode}`,
                                    }))
                                    .filter(d => d.name.toLowerCase().includes(search.toLowerCase()))
                            }}
                            className={lookupClass}
                            popperClassName={popperClass}
                            value={null}
                            equals={(a, b) => a.id === b.id}
                            makeLabel={a => a.name}
                            onSelect={(o) => {
                                if (value.miscItems?.find(v => v.id === o.id)) return;

                                editValue(old => Object.assign({}, old, {
                                    miscItems: [...(old.miscItems || []), o]
                                }))
                            }}
                            onMultiSelect={(o) => {
                                o = o.filter(oo => !value.miscItems?.find(v => v.id === oo.id));

                                editValue(old => Object.assign({}, old, {
                                    miscItems: [...(old.miscItems || []), ...o]
                                }))
                            }}
                            useNativeInput
                            placeholder="Search for misc product to add"
                            style={{
                                backgroundColor: "transparent",
                            }}
                        />}

                        <select value={value.logisticsMode} onChange={(e) => {
                            editValue(prev => ({...prev, logisticsMode: e.target.value as any}));
                        }}>
                            <option value="factory-key">Factory Keyed</option>
                            <option value="on-site">On-Site Keyed</option>
                        </select>
                        {isEditing && <>
                            <div style={{width: 4}} />
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() => {
                                    setValue(props.value);
                                    editCtx.onDone();
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                disabled={save.loading}
                                onClick={() => save.callback(value)}
                                endIcon={save.loading ? <CircularProgress size={14} color="inherit" /> : undefined}
                            >
                                Save
                            </Button>
                        </>}
                    </div>
                </td>
            </tr>
            {value.openings?.length === 0 && value.miscItems?.length === 0 && <tr className={keyingShortCodeCls}>
                <td colSpan={columnCount + props.labels.length}>
                    <div style={{
                        paddingLeft: leftIndent,
                    }}>
                        <i>No Openings or Misc Items</i>
                    </div>
                </td>
            </tr>}
            {value.openings?.map(o => <tr key={"opening-" + o.id} className={keyingShortCodeCls}>
                <td style={{
                    paddingLeft: leftIndent,
                }} colSpan={columnCount + props.labels.length}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                    }}>
                        <IconButton2 disabled={locked} style={{height: "1.2rem"}} onClick={() => {
                            editValue(old => Object.assign({}, old, {
                                openings: old.openings?.filter(op => op.id !== o.id)
                            }))
                        }}>
                            <RemoveCircleOutline fontSize="inherit"/>
                        </IconButton2>
                        <OpeningLabel value={o} />
                    </div>
                </td>
            </tr>)}
            {value.miscItems?.map(m => <tr key={"misc-" + m.id} className={keyingShortCodeCls}>
                <td style={{
                    paddingLeft: leftIndent,
                }} colSpan={columnCount + props.labels.length}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                    }}>
                        <IconButton2 disabled={locked} style={{height: "1.2rem"}} onClick={() => {
                            editValue(old => Object.assign({}, old, {
                                miscItems: old.miscItems?.filter(ms => ms.id !== m.id)
                            }))
                        }}>
                            <RemoveCircleOutline fontSize="inherit"/>
                        </IconButton2>
                        <div>
                            {m.name}
                        </div>
                    </div>
                </td>
            </tr>)}

            {all && <tr className={keyingShortCodeCls}>
                <td>
                    <div style={{
                        paddingLeft: leftIndent * 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                    }}>
                        <IconButton2 onClick={() => setShowAll(!showAll)} style={{
                            height: "1.2rem",
                        }}>
                            {showAll ? <KeyboardArrowDown fontSize="inherit"/> : <ChevronRight fontSize="inherit"/>}
                        </IconButton2>
                        <div>
                            All {showAll ? "" : `(${productBase.length} items)`}
                        </div>
                    </div>
                </td>
                <td className={alternatingColumnCss}>
                    <KeyingCheckbox selected={all.byOthers} onChange={(checked) => {
                        editValue(prev => ({
                            ...prev,
                            products: prev.products?.map(p => Object.assign({}, p, {
                                byOthers: checked,
                            })),
                        }));
                    }} />
                </td>
                {props.labels.map((l,index) => <td key={l.name} className={index % 2 === 1 ? alternatingColumnCss : ""}>
                    <KeyingCheckbox selected={all.labelsSelected.includes(l.name)} onChange={(checked) => {
                        const remove = all?.labelsSelected.includes(l.name);

                        editValue(prev => ({
                            ...prev,
                            products: prev.products?.map(p => Object.assign({}, p, {
                                labelsSelected: remove ? p.labelsSelected.filter(n => n !== l.name) : [...p.labelsSelected, l.name],
                            })),
                        }));
                    }} />
                </td>)}
                <td />
                <td />
                <td />
            </tr>}

            {showAll && productBase.map(pr => {

                const base = pr;
                const openingParts = products.filter(p => p.product === base.product && !!p.openingPart);

                return (
                    <>
                        {base && <tr className={keyingShortCodeCls}>
                            <td style={{}}>
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    paddingLeft: leftIndent * 3,
                                }}>
                                    <div>{base.productCategory} {base.productCode} {base.openingPart}</div>
                                    <IconPopupItem
                                        icon={<InfoOutlined />}
                                    >
                                        <table className={detailTable}>
                                            <thead>
                                            <tr>
                                                <th>Opening</th>
                                                <th>Qty</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {base.qtyMeta.map(q => (
                                                <tr>
                                                    <td>{q.name}</td>
                                                    <td style={{textAlign: "right"}}>{q.qty}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </IconPopupItem>
                                </div>
                            </td>
                            <td className={alternatingColumnCss}>
                                <KeyingCheckbox selected={base.byOthers} onChange={(checked) => {
                                    editValue(prev => {
                                        const prevProducts = prev.products || [];
                                        const prevAll = prevProducts.find(p => !p.product);

                                        const changes = prevProducts.map(p => {
                                            if (p.product === base.product && (p.id === base.id || !!p.openingPart)) {
                                                return Object.assign({}, p, {byOthers: checked});
                                            }
                                            if (!checked && prevAll && p.id === prevAll.id) {
                                                return Object.assign({}, p, {byOthers: checked});
                                            }
                                            return p;
                                        });

                                        return {...prev, products: changes};
                                    });
                                }} />
                            </td>
                            {props.labels.map((l,index) => <td key={l.name} className={index % 2 === 1 ? alternatingColumnCss : ""}>
                                <KeyingCheckbox selected={base.labelsSelected.includes(l.name)} onChange={(checked) => {
                                    const remove = base?.labelsSelected.includes(l.name);

                                    editValue(prev => {
                                        const prevProducts = prev.products || [];
                                        const prevAll = prevProducts.find(p => !p.product);

                                        const changes = prevProducts.map(p => {
                                            if (p.product === base.product && (p.id === base.id || !!p.openingPart)) {
                                                return Object.assign({}, p, {
                                                    labelsSelected: remove ? p.labelsSelected.filter(n => n !== l.name) : [...p.labelsSelected, l.name],
                                                });
                                            }
                                            if (remove && prevAll && p.id === prevAll.id) {
                                                return Object.assign({}, p, {
                                                    labelsSelected: p.labelsSelected.filter(n => n !== l.name),
                                                });
                                            }
                                            return p;
                                        });

                                        return {...prev, products: changes};
                                    });
                                }} />
                            </td>)}
                            <td />
                            <td style={{textAlign: "right"}}>
                                {base.qtyMeta?.reduce((a, b) => a + b.qty, 0)}
                            </td>
                            <td />
                        </tr>}

                        {openingParts?.map(p => <tr className={keyingShortCodeCls}>
                            <td style={{}}>
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    paddingLeft: leftIndent * 4,
                                }}>
                                    {ucWords(p.openingPart || "")}
                                </div>
                            </td>
                            <td className={alternatingColumnCss}>
                                <KeyingCheckbox selected={p.byOthers} onChange={(checked) => {
                                    editValue(prev => {
                                        const prevProducts = prev.products || [];
                                        const prevAll = prevProducts.find(pp => !pp.product);
                                        const prevBase = prevProducts.find(pp => pp.product === base.product && !pp.openingPart);

                                        const changes = prevProducts.map(pp => {
                                            if (pp.id === p.id) {
                                                return Object.assign({}, pp, {byOthers: checked});
                                            }
                                            if (!checked) {
                                                if (prevAll && pp.id === prevAll.id) {
                                                    return Object.assign({}, pp, {byOthers: checked});
                                                }
                                                if (prevBase && pp.id === prevBase.id) {
                                                    return Object.assign({}, pp, {byOthers: checked});
                                                }
                                            }
                                            return pp;
                                        });

                                        return {...prev, products: changes};
                                    });
                                }} />
                            </td>
                            {props.labels.map((l,index) => <td key={l.name} className={index % 2 === 1 ? alternatingColumnCss : ""}>
                                <KeyingCheckbox selected={p.labelsSelected.includes(l.name)} onChange={(checked) => {
                                    const remove = p?.labelsSelected.includes(l.name);

                                    editValue(prev => {
                                        const prevProducts = prev.products || [];
                                        const prevAll = prevProducts.find(pp => !pp.product);
                                        const prevBase = prevProducts.find(pp => pp.product === base.product && !pp.openingPart);

                                        const changes = prevProducts.map(pp => {
                                            if (pp.id === p.id) {
                                                return Object.assign({}, pp, {
                                                    labelsSelected: remove ? pp.labelsSelected.filter(n => n !== l.name) : [...pp.labelsSelected, l.name],
                                                });
                                            }
                                            if (remove) {
                                                if (prevAll && pp.id === prevAll.id) {
                                                    return Object.assign({}, pp, {
                                                        labelsSelected: pp.labelsSelected.filter(n => n !== l.name),
                                                    });
                                                }
                                                if (prevBase && pp.id === prevBase.id) {
                                                    return Object.assign({}, pp, {
                                                        labelsSelected: pp.labelsSelected.filter(n => n !== l.name),
                                                    });
                                                }
                                            }
                                            return pp;
                                        });

                                        return {...prev, products: changes};
                                    });
                                }} />
                            </td>)}
                            <td />
                            <td />
                            <td />
                        </tr>)}
                    </>
                )
            })}
        </>
    )
}

function OpeningLabel(props: { value: Pick<Keying2Opening, "name" | "locationTwo" | "baseBuilding" | "baseBuildingMissing"> }) {
    return (
        <div style={{display: "flex", alignItems: "baseline", gap: 6}}>
            <div>{props.value.name}</div>
            {props.value.locationTwo && <div style={{fontSize: "0.85em", color: grey["700"]}}>{props.value.locationTwo}</div>}
            {props.value.baseBuilding && <Chip label="Base Building" size="small" variant="outlined" style={{height: 18, fontSize: "0.75em"}} />}
            {props.value.baseBuildingMissing && <Tooltip title="This opening was removed or archived in the base building project, or the base building project is no longer linked">
                <WarningAmber color="warning" style={{fontSize: "1em", alignSelf: "center"}} />
            </Tooltip>}
        </div>
    )
}

const keyingShortCodeCls = "keying2-shortcode-edit-section"

const openingPopperClass = popperClass + " " + css({
    minWidth: 300,
})

export const leftIndent = 16

const detailTable = css({
    borderCollapse: "collapse",
    fontSize: "0.8rem",

    "& tr:nth-child(even)": {
        "& th, td": {
            backgroundColor: grey["200"],
        }
    },

    "& td,th": {
        paddingLeft: 2, paddingRight: 2,

        "&:last-child": {
            paddingRight: 4,
        },

        "&:first-child": {
            paddingLeft: 4,
        }
    },
})

const headerRow = css({
    "& > td" : {
        backgroundColor: grey["200"],
    }
})

const alternatingColumnCss = css({
    backgroundColor: grey["200"],
})
